import { useState } from 'react';
import { Link } from 'react-router-dom';

function Idx({ num, children }) {
  return <span className="cz-section-idx"><strong>{num}</strong>{children}</span>;
}

export default function Pricing() {
  const [annual, setAnnual] = useState(true);
  const mul = annual ? 0.85 : 1; // 15% off annual

  const tiers = [
    {
      code: 'I', name: 'Free', seats: 'Up to 20 employees',
      price: 0, priceLabel: 'Permanent free pilot',
      lede: 'Run a real calibration cycle without budget approval.',
      includes: ['Core reviews','Basic calibration','Scoring engine','Dynamic distribution','CSV export'],
      cta: 'Start free pilot', ctaTo: '/register',
      tone: 'cream',
    },
    {
      code: 'II', name: 'Starter', seats: '21–100 employees',
      price: 6 * mul, priceLabel: 'per employee / month',
      lede: 'Everything in Free, plus full 360 and PIP.',
      includes: ['All Free features','360 reviews (5 sources)','PIP tracker + IDPs','Goals (basic)','2 integrations','Email support'],
      cta: 'Start Starter', ctaTo: '/register',
      tone: 'cream',
    },
    {
      code: 'III', name: 'Growth', seats: '101–500 employees',
      price: 10 * mul, priceLabel: 'per employee / month',
      lede: 'The discipline kicks in: OKRs, surveys, integrations.',
      includes: ['All Starter','OKRs + key results','Weekly check-ins','Engagement surveys','Feedback hub','Jira / Git / HRMS','API access','Webhook events'],
      cta: 'Start Growth', ctaTo: '/register',
      tone: 'ink', // featured tier, dark card
      featured: true,
    },
    {
      code: 'IV', name: 'Scale', seats: '501–2000 employees',
      price: 14 * mul, priceLabel: 'per employee / month',
      lede: 'Intelligence layer + HRIS + Comp, all under one roof.',
      includes: ['All Growth','Native HRIS','Compensation planning','Succession + job board','AI intelligence layer','SAML SSO','Manager Effectiveness Score'],
      cta: 'Talk to sales', ctaTo: '/contact',
      tone: 'cream',
    },
    {
      code: 'V', name: 'Enterprise', seats: '2000+ employees',
      price: null, priceLabel: 'Custom',
      lede: 'SOC 2, data residency, dedicated CSM, SLA.',
      includes: ['All Scale','L&D (real LMS)','Advanced AI','Industry benchmarking','Bias audit certification','Data residency (EU / US)','99.95% SLA','Dedicated CSM'],
      cta: 'Request quote', ctaTo: '/contact',
      tone: 'cream',
    },
  ];

  const fmt = (p) => p === null || p === 0 ? null : `$${p.toFixed(p < 10 ? 2 : 0)}`;

  return (
    <>
      {/* ══ HERO ══════════════════════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 72, paddingBottom: 40 }}>
        <div className="cz-wrap">
          <div style={{ maxWidth: 960 }}>
            <Idx num="00">Pricing schedule</Idx>
            <h1 className="cz-display cz-display-xl" style={{ marginTop: 18 }}>
              One renewal.<br/>
              <em>One</em> invoice. One data model.
            </h1>
            <p className="cz-lede" style={{ marginTop: 24, maxWidth: 64 * 8 }}>
              The Lattice + Culture Amp + BambooHR stack costs the average 300-person
              company $23–37 per employee per month across three renewals and three
              integration surfaces. CalibZa does more for $10–14.
            </p>
          </div>

          {/* Billing toggle */}
          <div style={{ marginTop: 40, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="cz-mono-label">Billed</span>
            <div style={{ display: 'inline-flex', border: '1px solid var(--rule-soft)', background: 'var(--cream-warm)', padding: 2 }}>
              <button
                onClick={() => setAnnual(false)}
                style={{
                  padding: '10px 18px',
                  background: !annual ? 'var(--ink)' : 'transparent',
                  color: !annual ? 'var(--cream)' : 'var(--ink)',
                  fontSize: 13, fontWeight: 600, border: 0, cursor: 'pointer',
                  fontFamily: 'var(--ff-body)',
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                style={{
                  padding: '10px 18px',
                  background: annual ? 'var(--ink)' : 'transparent',
                  color: annual ? 'var(--cream)' : 'var(--ink)',
                  fontSize: 13, fontWeight: 600, border: 0, cursor: 'pointer',
                  fontFamily: 'var(--ff-body)',
                }}
              >
                Annual
              </button>
            </div>
            {annual && (
              <span className="cz-pill cz-pill-yellow" style={{ color: 'var(--ink)' }}>
                Save 15% · 2 months free
              </span>
            )}
          </div>
        </div>
      </section>

      {/* ══ TIER CARDS ════════════════════════════════════════════════ */}
      <section style={{ paddingBottom: 80 }}>
        <div className="cz-wrap">
          <div className="tier-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 18,
          }}>
            {tiers.slice(0, 3).map((t, i) => <TierCard key={t.name} t={t} fmt={fmt} annual={annual} />)}
          </div>
          <div className="tier-grid" style={{
            marginTop: 18, display: 'grid',
            gridTemplateColumns: '1fr 1fr', gap: 18,
          }}>
            {tiers.slice(3).map((t) => <TierCard key={t.name} t={t} fmt={fmt} annual={annual} />)}
          </div>
        </div>
      </section>

      {/* ══ § 02 · ADD-ONS ════════════════════════════════════════════ */}
      <section className="cz-section" style={{ background: 'var(--cream-warm)', borderBlock: '1px solid var(--rule-soft)' }}>
        <div className="cz-wrap">
          <Idx num="01">Add-on modules</Idx>
          <h2 className="cz-display cz-display-md" style={{ marginTop: 14, maxWidth: 820 }}>
            Don't upgrade for one feature. <em>Add</em> it.
          </h2>
          <p className="cz-body cz-body-muted" style={{ marginTop: 16, maxWidth: 54 * 8 }}>
            Add-ons attach to any paid tier. They share the same data model so the
            intelligence keeps compounding across modules.
          </p>

          <div className="addon-grid" style={{ marginTop: 40, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, border: '1px solid var(--rule-soft)', background: 'var(--rule-soft)' }}>
            {[
              ['HRIS',                  '+$2', 'records, org, time-off'],
              ['Compensation',          '+$2', 'bands, merit, audit'],
              ['Learning & Development','+$3', 'courses, IDPs, SCORM'],
              ['AI Intelligence Pack',  '+$4', 'attrition, promotion, bias'],
              ['Compliance Export',     '+$1', 'GDPR, SAR, PIP legal'],
            ].map(([name, price, body]) => (
              <div key={name} style={{
                background: 'var(--cream)', padding: '26px 22px',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <p className="cz-mono-label">{name}</p>
                <p style={{
                  fontFamily: 'var(--ff-display)', fontVariationSettings: '"opsz" 144, "SOFT" 80, "WONK" 0',
                  fontSize: 48, fontWeight: 400, margin: 0, lineHeight: 1,
                  letterSpacing: '-0.03em', color: 'var(--blue)',
                }}>{price}</p>
                <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0 }}>per employee / month</p>
                <p style={{ fontSize: 13, color: 'var(--ink)', margin: '8px 0 0' }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ § 02 · COMPARISON VS STACK ════════════════════════════════ */}
      <section className="cz-section">
        <div className="cz-wrap">
          <Idx num="02">What the competition actually costs</Idx>
          <h2 className="cz-display cz-display-md" style={{ marginTop: 14, maxWidth: 860 }}>
            The <em>real</em> unit economics of a multi-vendor HR stack.
          </h2>

          <div style={{ marginTop: 40, border: '1px solid var(--rule-soft)' }}>
            {[
              ['CalibZa — everything',              '$14',    '1',    'One renewal, one schema, compounding intelligence', true],
              ['Lattice + Culture Amp + BambooHR',  '$23–37', '3',    'Three renewals, three integration surfaces, no shared calibration engine', false],
              ['Betterworks + HiBob',               '$30–50+','2',    'No L&D, no dynamic distribution, enterprise-priced', false],
              ['Leapsome (all modules)',            '$15–25', '1',    'No calibration engine, no anti-gaming, no dynamic distribution', false],
              ['Spreadsheets + Google Forms',       '$0',     '0',    'Subjective, biased, inconsistent, no audit trail, legal exposure', false],
            ].map(([name, price, renewals, note, isUs], i) => (
              <div key={name} style={{
                display: 'grid',
                gridTemplateColumns: '260px 120px 100px 1fr',
                gap: 32, padding: '24px 28px',
                borderBottom: i === 4 ? 'none' : '1px solid var(--rule-soft)',
                background: isUs ? 'var(--blue-wash)' : 'var(--cream)',
                alignItems: 'center',
              }} className="cmp-row">
                <p style={{
                  fontFamily: 'var(--ff-display)', fontWeight: 500, fontSize: 18,
                  margin: 0, letterSpacing: '-0.015em',
                  color: isUs ? 'var(--blue)' : 'var(--ink)',
                }}>{name}</p>
                <p style={{
                  fontFamily: 'var(--ff-display)', fontStyle: 'italic',
                  fontVariationSettings: '"opsz" 48, "SOFT" 100, "WONK" 1',
                  fontSize: 28, margin: 0, color: 'var(--ink)',
                }}>{price}</p>
                <p style={{ fontFamily: 'var(--ff-mono)', fontSize: 12, color: 'var(--muted)', margin: 0 }}>{renewals} renewals</p>
                <p style={{ fontSize: 14, color: 'var(--ink)', margin: 0 }}>{note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ § 03 · FULL FEATURE MATRIX ═══════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 0 }}>
        <div className="cz-wrap">
          <Idx num="03">What's in each tier</Idx>
          <h2 className="cz-display cz-display-md" style={{ marginTop: 14, maxWidth: 720 }}>
            Full matrix.
          </h2>

          <div className="cmp-scroll" style={{ marginTop: 36, overflowX: 'auto', border: '1px solid var(--rule-soft)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
              <thead>
                <tr style={{ background: 'var(--cream-warm)' }}>
                  {['Feature','Free','Starter','Growth','Scale','Enterprise'].map((h, i) => (
                    <th key={h} style={{
                      padding: '14px 18px',
                      textAlign: i === 0 ? 'left' : 'center',
                      fontFamily: 'var(--ff-mono)', fontSize: 11, fontWeight: 500,
                      letterSpacing: '0.12em', textTransform: 'uppercase',
                      color: 'var(--muted)',
                      borderBottom: '1px solid var(--rule-soft)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Employee seats',         'Up to 20','Up to 100','Up to 500','Up to 2000','Unlimited'],
                  ['Core reviews',           '●','●','●','●','●'],
                  ['Calibration board',      '●','●','●','●','●'],
                  ['Scoring engine',         '●','●','●','●','●'],
                  ['Dynamic distribution',   '●','●','●','●','●'],
                  ['360 reviews (5 sources)','○','●','●','●','●'],
                  ['PIP + IDP',              '○','●','●','●','●'],
                  ['Goals / OKRs',           '○','◐','●','●','●'],
                  ['Engagement surveys',     '○','○','●','●','●'],
                  ['Feedback hub',           '○','○','●','●','●'],
                  ['Check-ins + 1:1 agendas','○','○','●','●','●'],
                  ['HRIS',                   '○','○','add-on','●','●'],
                  ['Compensation planning',  '○','○','add-on','●','●'],
                  ['Succession + job board', '○','○','○','●','●'],
                  ['AI intelligence layer',  '○','○','○','●','●'],
                  ['Manager Effectiveness',  '○','○','○','●','●'],
                  ['Industry benchmarking',  '○','○','○','○','●'],
                  ['Bias audit certification','○','○','○','○','●'],
                  ['Data residency (EU/US)', '○','○','○','○','●'],
                  ['SAML SSO',               '○','○','○','●','●'],
                  ['SLA',                    '—','—','—','99.9%','99.95%'],
                  ['Support',                'Community','Email','Priority email','Slack Connect','Dedicated CSM'],
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--rule-soft)' }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{
                        padding: '12px 18px',
                        textAlign: ci === 0 ? 'left' : 'center',
                        fontSize: 13.5, fontWeight: ci === 0 ? 500 : 600,
                        fontFamily: ci === 0 ? 'var(--ff-body)' : 'var(--ff-display)',
                        fontVariationSettings: ci > 0 ? '"opsz" 48, "SOFT" 60' : undefined,
                        color: 'var(--ink)',
                      }}>
                        {cell === '●' ? <span style={{ color: 'var(--blue)' }}>●</span> :
                         cell === '◐' ? <span style={{ color: 'var(--muted)' }}>◐</span> :
                         cell === '○' ? <span style={{ color: 'var(--rule)' }}>○</span> :
                         cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ══ § 04 · FAQ ════════════════════════════════════════════════ */}
      <section className="cz-section" style={{ background: 'var(--cream-warm)', borderTop: '1px solid var(--rule-soft)' }}>
        <div className="cz-wrap">
          <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 64 }}>
            <div>
              <Idx num="04">Questions, answered</Idx>
              <h2 className="cz-display cz-display-md" style={{ marginTop: 14 }}>
                The <em>fine</em> print.
              </h2>
            </div>

            <div>
              {[
                ['Can we really run the free tier forever?',
                 'Yes. Twenty seats, core calibration, full scoring engine. No time limit, no credit card. When you outgrow 20 employees, you pay for the next tier — nothing retroactive.'],
                ['What happens to our data on churn?',
                 "Your data is your data. Every tenant gets a self-service export to CSV + JSON covering every record. We don't hold migrations hostage."],
                ['Do you do custom contracts?',
                 'Yes — on Enterprise. Custom data residency, custom SLA, custom add-on bundles, custom billing. Talk to sales.'],
                ['Do the add-ons stack?',
                 'Stack freely on Starter or Growth. Scale and Enterprise already include HRIS, Compensation, Succession, and AI — so add-ons there are ignored.'],
                ['Is there a discount for non-profits?',
                 '50% off every tier (including add-ons) for registered 501(c)(3) or equivalent non-profits. Pro-bono seats available for education.'],
                ['How does the billing engine handle mid-cycle changes?',
                 'Stripe-prorated. Upgrades bill immediately for the prorated remainder of the period. Downgrades apply at the end of the current billing cycle.'],
                ['Do you offer a free trial of paid tiers?',
                 "30-day trial on Starter, Growth, and Scale. Enterprise trials are scoped during a kick-off call and typically last 60 days including the first calibration cycle."],
                ['Are annual contracts required?',
                 "Monthly is available on every tier. Annual saves 15%. Enterprise is typically annual but we'll discuss quarterly for larger commitments."],
              ].map(([q, a]) => (
                <details key={q} style={{
                  borderTop: '1px solid var(--rule-soft)',
                  padding: '18px 0',
                }}>
                  <summary style={{
                    fontFamily: 'var(--ff-display)', fontWeight: 500, fontSize: 18,
                    cursor: 'pointer', listStyle: 'none', letterSpacing: '-0.01em',
                    display: 'flex', justifyContent: 'space-between', gap: 16,
                  }}>
                    {q}
                    <span style={{ color: 'var(--blue)', fontStyle: 'italic', fontSize: 20 }}>+</span>
                  </summary>
                  <p style={{ marginTop: 12, color: 'var(--muted)', fontSize: 14.5, lineHeight: 1.65 }}>{a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ CTA ═══════════════════════════════════════════════════════ */}
      <section className="cz-section cz-section-dark" style={{ paddingBlock: 96, textAlign: 'center' }}>
        <div className="cz-wrap">
          <h2 className="cz-display cz-display-lg" style={{ maxWidth: 920, marginInline: 'auto' }}>
            Twenty seats free.<br/>
            <em style={{ color: 'var(--yellow)' }}>Calibration</em> included.
          </h2>
          <div style={{ display: 'flex', gap: 12, marginTop: 34, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="cz-btn cz-btn-cream cz-btn-lg">
              Start free pilot <span className="cz-btn-arrow">→</span>
            </Link>
            <Link to="/contact" className="cz-btn cz-btn-outline cz-btn-lg" style={{ boxShadow: 'inset 0 0 0 1px var(--cream)', color: 'var(--cream)' }}>
              Talk to sales
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 1024px) {
          .tier-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 700px) {
          .tier-grid { grid-template-columns: 1fr !important; }
          .addon-grid { grid-template-columns: 1fr 1fr !important; }
          .two-col { grid-template-columns: 1fr !important; gap: 32px !important; }
          .cmp-row { grid-template-columns: 1fr !important; gap: 8px !important; }
        }
      `}</style>
    </>
  );
}

function TierCard({ t, fmt, annual }) {
  const isInk = t.tone === 'ink';
  const price = fmt(t.price);
  return (
    <div style={{
      background: isInk ? 'var(--ink)' : 'var(--cream)',
      color: isInk ? 'var(--cream)' : 'var(--ink)',
      border: isInk ? 'none' : '1px solid var(--rule-soft)',
      padding: '30px 28px 32px',
      position: 'relative',
      display: 'flex', flexDirection: 'column', gap: 16,
      boxShadow: t.featured ? '6px 6px 0 var(--yellow)' : undefined,
    }}>
      <span style={{
        position: 'absolute', top: -1, right: -1,
        padding: '5px 10px',
        background: isInk ? 'var(--yellow)' : 'var(--ink)',
        color: isInk ? 'var(--ink)' : 'var(--cream)',
        fontFamily: 'var(--ff-mono)', fontSize: 10, letterSpacing: '0.12em',
      }}>TIER {t.code}</span>

      <div>
        <h3 style={{
          fontFamily: 'var(--ff-display)', fontWeight: 450, fontSize: 42,
          margin: 0, letterSpacing: '-0.02em', lineHeight: 1,
          fontVariationSettings: '"opsz" 144, "SOFT" 80, "WONK" 0',
        }}>{t.name}</h3>
        <p className="cz-mono-label" style={{ marginTop: 8, color: isInk ? 'var(--muted-ink)' : 'var(--muted)' }}>
          {t.seats}
        </p>
      </div>

      <div style={{ paddingBlock: 14, borderBlock: `1px solid ${isInk ? 'var(--rule-dark)' : 'var(--rule-soft)'}` }}>
        {price ? (
          <>
            <p style={{
              fontFamily: 'var(--ff-display)', fontWeight: 400,
              fontSize: 64, margin: 0, lineHeight: 1,
              letterSpacing: '-0.03em',
              fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 0',
              color: isInk ? 'var(--yellow)' : 'var(--blue)',
            }}>{price}</p>
            <p style={{ fontSize: 12, color: isInk ? 'var(--muted-ink)' : 'var(--muted)', margin: '4px 0 0', fontFamily: 'var(--ff-mono)', letterSpacing: '0.08em' }}>
              {t.priceLabel} {annual && t.price > 0 && '· billed annually'}
            </p>
          </>
        ) : t.price === 0 ? (
          <>
            <p style={{
              fontFamily: 'var(--ff-display)', fontWeight: 400, fontStyle: 'italic',
              fontSize: 56, margin: 0, lineHeight: 1,
              letterSpacing: '-0.03em', color: isInk ? 'var(--yellow)' : 'var(--blue)',
              fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
            }}>Free</p>
            <p style={{ fontSize: 12, color: isInk ? 'var(--muted-ink)' : 'var(--muted)', margin: '4px 0 0', fontFamily: 'var(--ff-mono)', letterSpacing: '0.08em' }}>
              {t.priceLabel}
            </p>
          </>
        ) : (
          <>
            <p style={{
              fontFamily: 'var(--ff-display)', fontWeight: 400, fontStyle: 'italic',
              fontSize: 48, margin: 0, lineHeight: 1,
              color: isInk ? 'var(--yellow)' : 'var(--blue)',
              fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
            }}>Custom</p>
            <p style={{ fontSize: 12, color: isInk ? 'var(--muted-ink)' : 'var(--muted)', margin: '4px 0 0', fontFamily: 'var(--ff-mono)', letterSpacing: '0.08em' }}>
              Scoped to fit
            </p>
          </>
        )}
      </div>

      <p style={{ fontFamily: 'var(--ff-display)', fontSize: 16, lineHeight: 1.4, margin: 0, color: isInk ? 'var(--cream)' : 'var(--ink)' }}>
        {t.lede}
      </p>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {t.includes.map(f => (
          <li key={f} style={{
            display: 'flex', gap: 10, alignItems: 'baseline',
            fontSize: 13.5, color: isInk ? 'var(--cream)' : 'var(--ink)',
          }}>
            <span style={{ color: isInk ? 'var(--yellow)' : 'var(--blue)', fontSize: 11, lineHeight: 1 }}>✓</span>
            {f}
          </li>
        ))}
      </ul>

      <Link
        to={t.ctaTo}
        className={isInk ? 'cz-btn cz-btn-cream' : 'cz-btn cz-btn-solid'}
        style={{ marginTop: 'auto', justifyContent: 'center' }}
      >
        {t.cta} <span className="cz-btn-arrow">→</span>
      </Link>
    </div>
  );
}
